// RXD API utility - lightweight type-safe wrapper around RXD API
// Based on Rustici Cross Domain (RXD) API documentation

export interface RXDBookmarkData {
	courseId: string;
	modulePosition: number; // Position within the course (0-based index)
	timestamp: number;
}

/**
 * RXD API interface
 * https://rustici-docs.s3.amazonaws.com/crossdomain/3.x/API.html
 */
export interface RXDApi {
	GetStudentID(): string;
	GetStudentName(): string;
	/**
     * Returns the mode in which the content was launched. Possible values are:
     * - normal: The content was launched intending to record full status and/or scoring data
     * - browse: The content was launched expecting to not record status and/or scoring data
     * - review: The content was launched having already recorded status and/or scoring data, not expecting it to be updated
     * Calling RXD status and/or scoring functions will trigger messages to be passed to the LMS and that LMS may choose to accept or reject them based on the lesson mode that was provided during launch.
     * @returns lesson mode: 'normal', 'browse', or 'review'
     */
	GetLessonMode(): "normal" | "browse" | "review";
	/**
     * This function returns the last value that was saved using SetBookmark. If SetBookmark was not called previously, this function returns an empty string. This function is useful to call at the beginning of the content to retrieve the user’s last location and give him/her the option to return to that location.
     * @returns bookmark string or empty string if not available
     */
	GetBookmark(): string;
	/**
     * Saves a string that represents the user’s current location in the content. This string can be formatted however you choose. This function should be called periodically as the user progresses through the content. Note for SCORM 1.2 based proxy packages the string has a limit of 255 characters. LMSs may restrict the size for other standards as well.
     * @param val - The bookmark string to save
     */
	SetBookmark(val: string): void;
	/**
     * This function returns the last value that was saved to the suspend data bucket. If SetSuspendData was not called previously, or there was no suspend data available on launch, this function returns an empty string.
     * @returns suspend data string or empty string if not available
     */
	GetSuspendData(): string;
	/**
     * This function allows you to save a string of data. This data can represent anything; it is just an arbitrary chunk of data. This function is useful for saving internal state data that does not correspond to any other data element. Note for SCORM 1.2 based proxy packages the string has a limit of 4000 characters. LMSs may restrict the size for other standards as well.
     * @param val - The suspend data to save
     */
	SetSuspendData(val: string): void;
	Initialize(): void;
	SetScore(score: number, max?: number, min?: number): void;
	/**
     * Returns the current lesson status of the content. Possible values are:
     * - passed: The user completed the content with a score sufficient to pass.
     * - failed: The user completed the content but his/her score was not sufficient to pass.
     * - completed: The user completed the content.
     * - incomplete: The user began the content but did not complete it.
     * - browsed: The user looked at the content but was not making a recorded attempt at it.
     * - not_attempted: The user has not started the content.
     * - unknown: The content received an unrecognized status, or the status was reset via ResetStatus  
     * @returns lesson status: 'passed', 'failed', 'completed', 'incomplete', 'browsed', 'not_attempted', or 'unknown'
     */
	GetStatus(): "passed" | "failed" | "completed" | "incomplete" | "browsed" | "not_attempted" | "unknown";
	/**
	 * You can optionally call this function to indicate that the user has made sufficient progress in the content to be considered complete.
	 * This function is useful for content that has a final diploma or confirmation page.
	 * When that final page is reached, you should call SetReachedEnd. After calling this function a call to GetStatus will return completed unless the content was previously marked as passed or failed.
	 * @returns void
	 */
	SetReachedEnd(): void;
	SetPassed(): void;
	SetFailed(): void;
	ResetStatus(): void;
	/**
	 * This function will pass control back to the LMS and trigger the content window to be closed.
	 */
	ConcedeControl(): void;
}

export interface RXDWindow extends Window {
	RXD: RXDApi;
}

export interface RXDParentWindow extends Window {
	RXD: RXDApi;
}

/**
 * Get the RXD API from the parent window
 * @returns RXDApi instance or null if not available
 */
export const getRXDApi = (): RXDApi | null => {
	try {
		if (typeof window !== "undefined" && window.parent) {
			const parentWindow = window.parent as RXDParentWindow;
			const rxd = parentWindow.RXD || null;

			return rxd;
		}
		// eslint-disable-next-line no-console
		console.warn("RXD API not available: window.parent not accessible");
		return null;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("RXD API not available: Error accessing parent window", error);
		return null;
	}
};

/**
 * Check if RXD API is available
 * @returns boolean indicating if RXD API is accessible
 */
export const isRXDAvailable = (): boolean => {
	const rxd = getRXDApi();
	if (rxd !== null) {
		return true;
	}

	// Check if we're in a real LMS environment
	const isInIframe = window !== window.parent;
	const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
	const isDevelopment = process.env.NODE_ENV === "development";

	// If we're in an iframe but not on localhost, we should have RXD API
	if (isInIframe && !isLocalhost) {
		// eslint-disable-next-line no-console
		console.warn("RXD API not available in LMS environment - this may indicate a configuration issue");
		return false;
	}

	// Only simulate in development/localhost
	if (isDevelopment || isLocalhost) {
		// eslint-disable-next-line no-console
		console.log("RXD API not available in development mode, simulating availability");
		return true;
	}

	return false;
};

/**
 * Get student ID from RXD
 * @returns student ID string or empty string if not available
 */
export const getStudentID = (): string => {
	const rxd = getRXDApi();
	return rxd?.GetStudentID() || "";
};

/**
 * Get student name from RXD
 * @returns student name in "Last Name,First Name" format or empty string
 */
export const getStudentName = (): string => {
	const rxd = getRXDApi();
	return rxd?.GetStudentName() || "";
};

/**
 * Get lesson mode from RXD
 * @returns lesson mode: 'normal', 'browse', or 'review'
 */
export const getLessonMode = (): "normal" | "browse" | "review" => {
	const rxd = getRXDApi();
	return rxd?.GetLessonMode() || "normal";
};

/**
 * Get bookmark string from RXD
 * @returns bookmark string or empty string if not available
 */
export const getBookmarkString = (): string => {
	const rxd = getRXDApi();
	return rxd?.GetBookmark() || "";
};

/**
 * Set bookmark string in RXD
 * @param data - The bookmark string to save
 */
export const setBookmarkString = (data: string): void => {
	const rxd = getRXDApi();
	rxd?.SetBookmark(data);
};

/**
 * Get suspend data from RXD
 * @returns suspend data string or empty string if not available
 */
export const getSuspendData = (): string => {
	const rxd = getRXDApi();
	if (rxd && typeof rxd.GetSuspendData === "function") {
		return rxd.GetSuspendData();
	}

	// Check if we should simulate (development/localhost only)
	const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
	const isDevelopment = process.env.NODE_ENV === "development";

	if (isDevelopment || isLocalhost) {
		try {
			const data = localStorage.getItem("rxd_suspend_data") || "";
			// eslint-disable-next-line no-console
			console.log("RXD GetSuspendData: Simulated get from localStorage", data);
			return data;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.warn("RXD GetSuspendData: Failed to get from localStorage", error);
			return "";
		}
	}

	return "";
};

/**
 * Set suspend data in RXD
 * @param data - The suspend data to save
 */
export const setSuspendData = (data: string): void => {
	const rxd = getRXDApi();
	if (rxd && typeof rxd.SetSuspendData === "function") {
		try {
			rxd.SetSuspendData(data);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.warn("RXD SetSuspendData: Error", error);
		}
	} else {
		// Check if we should simulate (development/localhost only)
		const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
		const isDevelopment = process.env.NODE_ENV === "development";

		if (isDevelopment || isLocalhost) {
			try {
				localStorage.setItem("rxd_suspend_data", data);
				// eslint-disable-next-line no-console
				console.log("RXD SetSuspendData: Simulated save to localStorage", data);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn("RXD SetSuspendData: Failed to save to localStorage", error);
			}
		} else {
			// eslint-disable-next-line no-console
			console.warn("RXD SetSuspendData: Method not available");
		}
	}
};

/**
 * Initialize RXD communication
 */
export const initializeRXD = (): void => {
	const rxd = getRXDApi();
	if (rxd && typeof rxd.Initialize === "function") {
		try {
			rxd.Initialize();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("RXD Initialize: Error", error);
		}
	} else {
		// eslint-disable-next-line no-console
		console.warn("RXD Initialize: Method not available");
	}
};

export const setReachedEnd = (): void => {
	const rxd = getRXDApi();
	rxd?.SetReachedEnd();
};

export const setPassed = (): void => {
	const rxd = getRXDApi();
	rxd?.SetPassed();
};

export const concedeControl = (): void => {
	const rxd = getRXDApi();
	rxd?.ConcedeControl();
};

/**
 * Set score in RXD
 * @param score - The score to set (0-100)
 */
export const setScore = (score: number, max = 100.0, min = 0.0): void => {
	const rxd = getRXDApi();
	rxd?.SetScore(score, max, min);
};